<style lang="scss" scoped>
@use "./src/components/vue-select-user/scss/style";
</style>

<template>
  <div
    :dir="dir"
    class="fts-dropdown-user__select dropdown v-select"
    :class="dropdownClasses"
    ref="vSelectUser"
  >
    <div ref="toggle" @mousedown.prevent="toggleDropdown" class="vs-toggle">
      <div class="vs-selected-container" ref="selectedOptions">
        <!-- multiple -->
        <template v-if="multiple">
          <slot
            v-for="option in valueAsArray"
            name="selected-option-container"
            :option="(typeof option === 'object') ? option : { [getOptionLabelKey || label] : option }"
            :deselect="deselect"
            :multiple="multiple"
            :disabled="disabled"
          >
            <div
              :key="option.id"
              class="vs-selected-tag vs-selected-tag--background"
              v-if="text || option"
            >
              <slot
                name="selected-option"
                :option="(typeof option === 'object') ? option : { [getOptionLabelKey || label] : option }"
              >
                <span class="vs-selected-tag-label">{{getOptionLabel(option)}}</span>
              </slot>
              <font-awesome-icon
                :icon="['fas', 'times']"
                class="vs-action-clear"
                @click="deselect(option)"
                v-show="!disabled"
              />
            </div>
          </slot>
        </template>
        <!-- single -->
        <slot
          name="selected-option-container"
          :text="text"
          :deselect="deselect"
          :multiple="multiple"
          :disabled="disabled"
          v-else
        >
          <div class="vs-selected-option" v-if="text">
            <div class="vs-selected-tag">
              <slot name="selected-option" :text="text">
                {{text}}
              </slot>
            </div>
          </div>
        </slot>
        <!-- search -->
        <input
          ref="search"
          v-model.trim="search"
          class="vs-input"
          type="search-user"
          autocomplete="off"
          :disabled="disabled"
          :placeholder="searchPlaceholder"
          :tabindex="tabindex"
          :readonly="!searchable"
          :id="inputId"
          role="combobox"
          :aria-expanded="dropdownOpen"
          aria-label="Search for option"
          @compositionstart="() => (isComposing = true)"
          @compositionend="() => (isComposing = false)"
          @keydown="onSearchKeyDown"
          @keypress="onSearchKeyPress"
          @blur="onSearchBlur"
          @focus="onSearchFocus"
          @input="onSearchInput"
        />
      </div>
      <!-- spinner -->
      <slot name="spinner">
        <div class="vs-spinner" v-show="mutableLoading || loadingMore">Loading...</div>
      </slot>
      <!-- single:action clear -->
      <button
        type="button"
        class="vs-action-clear"
        title="Clear selection"
        v-show="showClearButton && !disabled"
        :disabled="disabled"
        tabindex="-1"
        @click="clearSelection"
      >
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
    </div>
    <!-- dropdown-menu -->
    <transition :name="transition">
      <ul
        ref="dropdownMenu"
        class="fts-dropdown__menu fts-scrollbar"
        :class="{
          'append-to-body': appendToBody,
          'multiple': multiple,   
          'comfort-design': comfortDesign,
        }"
        :style="{ 'max-height': maxHeight }"
        role="listbox"
        @scroll="onDropdownScroll"
        @mousedown.prevent="onMousedown"
        @mouseup="onMouseUp"
        v-if="dropdownOpen"
        v-append-to-body
      >
        <li
          aria-hidden="true"
          v-if="virtualTopSpacerHeight"
          :style="virtualSpacerStyle(virtualTopSpacerHeight)"
        />
        <!-- options -->
        <li
          class="fts-dropdown__option"
          role="option"
          v-for="item in renderedOptions"
          :key="getRenderedOptionKey(item)"
          :data-option-index="item.index"
          :class="{
            active: isOptionSelected(item.option),
            highlight: item.index === typeAheadPointer,
          }"
          @mouseover="typeAheadPointer = item.index"
          @mousedown.prevent.stop="select(item.option)"
        >
          <a>
            <slot
              name="option"
              v-bind="typeof item.option === 'object' ? item.option : { [label]: item.option }"
            >
              {{ getOptionLabel(item.option) }} &nbsp;
            </slot>
          </a>
        </li>
        <li
          aria-hidden="true"
          v-if="virtualBottomSpacerHeight"
          :style="virtualSpacerStyle(virtualBottomSpacerHeight)"
        />
        <!-- no option -->
        <li class="fts-dropdown__helper" v-if="!filteredOptions.length">
          <slot name="no-options">Sorry, no matching options.</slot>
        </li>
        <!-- add new -->
        <li
          class="fts-dropdown__helper action"
          @click="addNew"
          v-if="canAddNew && connectionId"
        >
          <slot name="add-new">
            <font-awesome-icon :icon="['fas', 'plus']" fixed-width class="helper-icon" />
            Add New
          </slot>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script type="text/babel">
import pointerScroll from "../mixins/pointerScroll";
import typeAheadPointer from "../mixins/typeAheadPointer";
import ajax from "../mixins/ajax";
import appendToBody from "../directives/appendToBody";
import { createPopper } from '@popperjs/core';
import sortAndStringify from "../utility/sortAndStringify";

import commons from "../../commons/commons";

export default {
  mixins: [pointerScroll, typeAheadPointer, ajax],
  directives: {appendToBody},
  props: {
    text: { default: null },
    /**
     * Append the dropdown element to the end of the body
     * and size/position it dynamically. Use it if you have
     * overflow or z-index issues.
     * @type {Boolean}
     */
    appendToBody: {
      type: Boolean,
      default: false
    },
    /**
     * When `appendToBody` is true, this function is responsible for
     * positioning the drop down list.
     *
     * If a function is returned from `calculatePosition`, it will
     * be called when the drop down list is removed from the DOM.
     * This allows for any garbage collection you may need to do.
     *
     * @since v3.7.0
     * @see http://vue-select.org/guide/positioning.html
     */
    calculatePosition: {
      type: Function,
      /**
       * @param dropdownList {HTMLUListElement}
       * @param component {Vue} current instance of vue select
       * @param width {string} calculated width in pixels of the dropdown menu
       * @param top {string} absolute position top value in pixels relative to the document
       * @param left {string} absolute position left value in pixels relative to the document
       * @return {function|void}
       */
      default(dropdownList, component, {width, top, left}) {
        if (this.appendToBody) {
          /* set style */
          dropdownList.style.width = width;
          /* create popper */
          this.popperInstance = createPopper(component.$refs.toggle, dropdownList, {
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'toggleClass',
                enabled: true,
                phase: 'write',
                fn ({state}) {
                  component.$el.classList.toggle('drop-up', state.placement === 'top')
                },
              }]
          });
          /* destory popper */
          return () => {
            if (this.popperInstance) {
              this.popperInstance.destroy();
              this.popperInstance = null;
            }
          }
        } else {
          dropdownList.style.top = top;
          dropdownList.style.left = left;
          dropdownList.style.width = width;
        }
      }
    },
    /**
     * Contains the currently selected value. Very similar to a
     * `value` attribute on an <input>. You can listen for changes
     * using 'change' event using v-on
     * @type {Object||String||null}
     */
    value: {
      default: null,
    },
    /**
     * An array of strings or objects to be used as dropdown choices.
     * If you are using an array of objects, vue-select will look for
     * a `label` key (ex. [{label: 'This is Foo', value: 'foo'}]). A
     * custom label key can be set with the `label` prop.
     * @type {Array}
     */
    options: {
      type: Array,
      default() {
        return [];
      },
    },
    /**
     * Disable the entire component.
     * @type {Boolean}
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Can the user clear the selected property?
     * @type {Boolean}
     */
    clearable: {
      type: Boolean,
      default: true,
    },
    /**
     * Sets the max-height property on the dropdown list.
     * @deprecated
     * @type {String}
     */
    maxHeight: {
      type: String,
      default: "400px",
    },
    /**
     * Enable/disable filtering the options.
     * @type {Boolean}
     */
    searchable: {
      type: Boolean,
      default: true,
    },
    /**
     * Equivalent to the `multiple` attribute on a `<select>` input.
     * @type {Boolean}
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * Equivalent to the `placeholder` attribute on an `<input>`.
     * @type {String}
     */
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * Sets a Vue transition property on the `.dropdown-menu`. vue-select
     * does not include CSS for transitions, you'll need to add them yourself.
     * @type {String}
     */
    transition: {
      type: String,
      default: "fade",
    },
    /**
     * Enables/disables clearing the search text when an option is selected.
     * @type {Boolean}
     */
    clearSearchOnSelect: {
      type: Boolean,
      default: true,
    },
    /**
     * Close a dropdown when an option is chosen. Set to false to keep the dropdown
     * open (useful when combined with multi-select, for example)
     * @type {Boolean}
     */
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    /**
     * Tells vue-select what key to use when generating option
     * labels when each `option` is an object.
     * @type {String}
     */
    label: {
      type: String,
      default: "null",
    },
    /**
     * Tells vue-select what key to use when generating option
     * values when each `option` is an object.
     * @type {String}
     */
    index: {
      type: String,
      default: null,
    },
    /**
     * When working with objects, the reduce
     * prop allows you to transform a given
     * object to only the information you
     * want passed to a v-model binding
     * or @input event.
     */
    reduce: {
      type: Function,
      default: option => option
    },
    /**
     * @type {String}
     */
    reduceKey: {
      type: String,
      default: 'id'
    },
    /**
     * For option display only to replace [this.label]
     * @type {String}
     */
    getOptionLabelKey: String,
    /**
     * Callback to generate the label text. If {option}
     * is an object, returns option[this.label] by default.
     *
     * Label text is used for filtering comparison and
     * displaying. If you only need to adjust the
     * display, you should use the `option` and
     * `selected-option` slots.
     *
     * @type {Function}
     * @param  {Object || String} option
     * @return {String}
     */
    getOptionLabel: {
      type: Function,
      default(option) {
        if (
          this.getOptionLabelKey
          && option.hasOwnProperty(this.getOptionLabelKey)
          && option[this.getOptionLabelKey]
        ) {
          return option[this.getOptionLabelKey];
        } else if (
          option.hasOwnProperty('optionName')
          && option.optionName
        ) {
          option = option.optionName;
        }

        if (this.index) {
          option = this.findOptionByIndexValue(option);
        }

        if (typeof option === "object") {
          let result = null;
          if (this.label && this.label.indexOf(".") > -1) {
            var fields = this.label.split(".");
            result = option;
            for (let i = 0; i < fields.length; i++) {
              result = result[fields[i]];

              if (result == null) {
                result = null;
                break;
              }
            }

            return result;
          } else {
            result = option[this.label];
            return result;
          }

          if (!option.hasOwnProperty(this.label)) {
            return console.warn(
              `[vue-select warn]: Label key "option.${this.label}" does not` +
                ` exist in options object ${JSON.stringify(option)}.\n` +
                "http://sagalbot.github.io/vue-select/#ex-labels"
            );
          }
          return option[this.label];
        }
        return option;
      },
    },
    // /**
    //  * An optional callback function that is called each time the selected
    //  * value(s) change. When integrating with Vuex, use this callback to trigger
    //  * an action, rather than using :value.sync to retreive the selected value.
    //  * @type {Function}
    //  * @param {Object || String} val
    //  */
    // onChange: {
    //   type: Function,
    //   default: function (val) {
    //     this.$emit("input", val);
    //   },
    // },
    /**
     * Select the current value if selectOnTab is enabled
     */
    onTab: {
      type: Function,
      default: function() {
        if (this.selectOnTab && !this.isComposing) {
          this.typeAheadSelect();
        }
      }
    },
    /**
     * Enable/disable creating options from searchInput.
     * @type {Boolean}
     */
    taggable: {
      type: Boolean,
      default: false,
    },
    /**
     * Set the tabindex for the input field.
     * @type {Number}
     */
    tabindex: {
      type: Number,
      default: null,
    },
    /**
     * When true, newly created tags will be added to
     * the options list.
     * @type {Boolean}
     */
    pushTags: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, existing options will be filtered
     * by the search text. Should not be used in conjunction
     * with taggable.
     * @type {Boolean}
     */
    filterable: {
      type: Boolean,
      default: true,
    },
    /**
     * Callback to determine if the provided option should
     * match the current search text. Used to determine
     * if the option should be displayed.
     * @type   {Function}
     * @param  {Object || String} option
     * @param  {String} label
     * @param  {String} search
     * @return {Boolean}
     */
    filterBy: {
      type: Function,
      default(option, label, search) {
        return (label || "").toLowerCase().indexOf(search.toLowerCase()) > -1;
      },
    },
    /**
     * Callback to filter results when search text
     * is provided. Default implementation loops
     * each option, and returns the result of
     * this.filterBy.
     * @type   {Function}
     * @param  {Array} list of options
     * @param  {String} search text
     * @param  {Object} vSelect instance
     * @return {Boolean}
     */
    filter: {
      type: Function,
      default(options, search) {
        return options.filter((option) => {
          let label = this.getOptionLabel(option);
          if (typeof label === "number") {
            label = label.toString();
          }
          return this.filterBy(option, label, search);
        });
      },
    },
    /**
     * User defined function for adding Options
     * @type {Function}
     */
    createOption: {
      type: Function,
      default(newOption) {
        if (typeof this.mutableOptions[0] === "object") {
          newOption = { [this.label]: newOption };
        }
        this.$emit("option:created", newOption);
        return newOption;
      },
    },
    /**
     * When false, updating the options will not reset the selected value. Accepts
     * a `boolean` or `function` that returns a `boolean`. If defined as a function,
     * it will receive the params listed below.
     *
     * @since 3.4 - Type changed to {Boolean|Function}
     *
     * @type {Boolean|Function}
     * @param {Array} newOptions
     * @param {Array} oldOptions
     * @param {Array} selectedValue
     */
    resetOnOptionsChange: {
      default: false,
      validator: (value) => ['function', 'boolean'].includes(typeof value),
    },
    /**
     * If search text should clear on blur
     * @return {Boolean} True when single and clearSearchOnSelect
     */
    clearSearchOnBlur: {
      type: Function,
      default: function ({ clearSearchOnSelect, multiple }) {
        return clearSearchOnSelect && !multiple;
      },
    },
    /**
     * Disable the dropdown entirely.
     * @type {Boolean}
     */
    noDrop: {
      type: Boolean,
      default: false,
    },
    /**
     * Sets the id of the input element.
     * @type {String}
     * @default {null}
     */
    inputId: {
      type: String,
    },
    /**
     * Sets RTL support. Accepts 'ltr', 'rtl', 'auto'.
     * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/dir
     * @type {String}
     * @default 'auto'
     */
    dir: {
      type: String,
      default: "auto",
    },
    /**
     * When true, hitting the 'tab' key will select the current select value
     * @type {Boolean}
     * @deprecated since 3.3 - use selectOnKeyCodes instead
     */
    selectOnTab: {
      type: Boolean,
      default: false,
    },
    /**
     * Keycodes that will select the current option.
     * @type {Array}
     */
    selectOnKeyCodes: {
      type: Array,
      default: () => [
        // enter
        13,
      ],
    },
    /**
     * Used to modify the default keydown events map
     * for the search input. Can be used to implement
     * custom behaviour for key presses.
     */
    mapKeydown: {
      type: Function,
      /**
       * @param map {Object}
       * @param vm {VueSelect}
       * @return {Object}
       */
      default: (map, vm) => map,
    },
    /**
     * Generate a unique identifier for each option. If `option`
     * is an object and `option.hasOwnProperty('id')` exists,
     * `option.id` is used by default, otherwise the option
     * will be serialized to JSON.
     *
     * If you are supplying a lot of options, you should
     * provide your own keys, as JSON.stringify can be
     * slow with lots of objects.
     *
     * The result of this function *must* be unique.
     *
     * @type {Function}
     * @param  {Object || String} option
     * @return {String}
     */
    getOptionKey: {
      type: Function,
      default(option) {
        if (typeof option !== 'object') {
          return option;
        }

        try {
          return option.hasOwnProperty(this.reduceKey)
            ? option[this.reduceKey]
            : sortAndStringify(option);
        } catch (e) {
          const warning =
            `[vue-select warn]: Could not stringify this option ` +
            `to generate unique key. Please provide'getOptionKey' prop ` +
            `to return a unique key for each option.\n` +
            'https://vue-select.org/api/props.html#getoptionkey'
          return console.warn(warning, option, e)
        }
      },
    },
    /**
     * @type {Boolean}
     */
    canAddNew: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {String || Number}
     */
    connectionId: [String, Number],
    /**
     * @type {Boolean}
     */
    comfortDesign: Boolean,
    error: Boolean,
    virtualized: {
      type: Boolean,
      default: true,
    },
    virtualizationThreshold: {
      type: Number,
      default: 150,
    },
    virtualizationOverscan: {
      type: Number,
      default: 6,
    },
  },
  data() {
    return {
      popperInstance: null,
      search: "",
      open: false,
      isComposing: false,
      mutableOptions: [],
      virtualScrollTop: 0,
      dropdownViewportHeight: 0,
      measuredOptionHeight: 40,
    };
  },
  model: {
    prop: "value",
    event: "updateValue",
  },
  watch: {
    // /**
    //  * When the value prop changes, update
    //  * the internal mutableValue.
    //  * @param  {mixed} val
    //  * @return {void}
    //  */
    // value(val) {
    //   this.mutableValue = val;
    // },

    // /**
    //  * Maybe run the onChange callback.
    //  * @param  {string|object} val
    //  * @param  {string|object} old
    //  * @return {void}
    //  */
    // mutableValue(val, old) {
    //   if (this.multiple) {
    //     this.onChange ? this.onChange(val) : null;
    //   } else {
    //     this.onChange && val !== old ? this.onChange(val) : null;
    //   }
    // },
    /**
     * When options change, update
     * the internal mutableOptions.
     * @param  {array} val
     * @return {void}
     */
    options(val) {
      this.mutableOptions = val;
    },
    /**
     * Maybe reset the mutableValue
     * when mutableOptions change.
     * @return {[type]} [description]
     */
    mutableOptions() {
      if (!this.taggable && this.resetOnOptionsChange) {
        this.mutableValue = this.multiple ? [] : null;
      }
    },
    /**
     * Always reset the value when
     * the multiple prop changes.
     * @return {void}
     */
    multiple() {
      this.clearSelection();
    },
    open(val) {
      if (!val) {
        this.focusSelf();
      } else {
        this.$nextTick(() => {
          this.resetVirtualScroll();
          this.updateVirtualMetrics();
        });
      }
    },
    search(search) {
      if (search.length) {
        this.open = true;
      }

      this.$nextTick(() => {
        this.resetVirtualScroll();
        this.updateVirtualMetrics();
      });
    },
    filteredOptions() {
      this.$nextTick(() => {
        this.updateVirtualMetrics();
      });
    }
  },
  created() {
    this.mutableOptions = this.options.slice(0);
    this.mutableLoading = this.loading;
    
    this.$on("option:created", this.maybePushTag);
  },
  methods: {
    addNew() {
      const guid = commons.newUUID();
      window.open('/#/list/' + this.connectionId + '?a=create&dialog=' + guid + '&text=' + encodeURIComponent(this.search));
      window.newAddedCallback = data => {
        this.$emit("addedNew", data);
      }
    },
    getDropdownHeightLimit() {
      const parsedMaxHeight = parseInt(this.maxHeight, 10);
      return Number.isFinite(parsedMaxHeight) ? parsedMaxHeight : 400;
    },
    resetVirtualScroll() {
      this.virtualScrollTop = 0;

      if (this.$refs.dropdownMenu) {
        this.$refs.dropdownMenu.scrollTop = 0;
      }
    },
    updateVirtualMetrics() {
      const dropdownMenu = this.$refs.dropdownMenu;
      if (!dropdownMenu) {
        return;
      }

      this.dropdownViewportHeight = dropdownMenu.clientHeight || this.getDropdownHeightLimit();
      this.virtualScrollTop = dropdownMenu.scrollTop || 0;

      const optionEl = dropdownMenu.querySelector("[data-option-index]");
      if (optionEl) {
        const optionHeight = Math.ceil(optionEl.getBoundingClientRect().height);

        if (optionHeight > 0) {
          this.measuredOptionHeight = optionHeight;
        }
      }
    },
    virtualSpacerStyle(height) {
      return {
        height: `${height}px`,
        padding: 0,
        margin: 0,
        border: 0,
        pointerEvents: "none",
      };
    },
    getRenderedOptionKey(item) {
      const optionKey = this.getOptionKey(item.option);
      return optionKey == null ? item.index : `${optionKey}-${item.index}`;
    },
    onDropdownScroll() {
      const dropdownMenu = this.$refs.dropdownMenu;

      if (dropdownMenu) {
        this.virtualScrollTop = dropdownMenu.scrollTop;
        this.dropdownViewportHeight = dropdownMenu.clientHeight;
      }

      this.loadMore();
    },
    focusSelf() {
      this.$refs.vSelectUser.focus();
    },
    maybeAdjustScroll() {
      const dropdownMenu = this.$refs.dropdownMenu;

      if (!dropdownMenu || this.typeAheadPointer < 0) {
        return;
      }

      if (this.shouldVirtualizeOptions) {
        const optionTop = this.typeAheadPointer * this.virtualOptionHeight;
        const optionBottom = optionTop + this.virtualOptionHeight;
        const viewportTop = dropdownMenu.scrollTop;
        const viewportBottom = viewportTop + dropdownMenu.clientHeight;

        if (optionTop < viewportTop) {
          dropdownMenu.scrollTop = optionTop;
        } else if (optionBottom > viewportBottom) {
          dropdownMenu.scrollTop = optionBottom - dropdownMenu.clientHeight;
        }

        return;
      }

      const optionEl = dropdownMenu.querySelector(`[data-option-index="${this.typeAheadPointer}"]`) || false;

      if (optionEl) {
        const bounds = this.getDropdownViewport();
        const { top, bottom, height } = optionEl.getBoundingClientRect();

        if (top < bounds.top) {
          dropdownMenu.scrollTop = optionEl.offsetTop;
        } else if (bottom > bounds.bottom) {
          dropdownMenu.scrollTop = optionEl.offsetTop - (bounds.height - height);
        }
      }
    },
    /**
     * Select a given option.
     * @param  {Object|String} option
     * @return {void}
     */
    select(option) {
      this.$emit('option:selecting', option);
      if (!this.isOptionSelected(option)) {
        if (this.taggable && !this.optionExists(option)) {
          option = this.createOption(option);
        }
        if (this.index) {
          if (!option.hasOwnProperty(this.index)) {
            return console.warn(
              `[vue-select warn]: Index key "option.${this.index}" does not` +
                ` exist in options object ${JSON.stringify(option)}.`
            );
          }
          option = option[this.index];
        }

        const reduceValue = this.reduce(option);

        if (this.multiple && !this.mutableValue) {
          this.mutableValue = [reduceValue];
        } else if (this.multiple) {
          this.mutableValue.push(reduceValue);
        } else {
          this.mutableValue = reduceValue;
        }
        this.$emit('option:selected', option);
      }
      this.onAfterSelect(option);
    },
    /**
     * De-select a given option.
     * @param  {Object|String} option
     * @return {void}
     */
    deselect(option) {
      this.$emit('option:deselecting', option);
      if (this.multiple) {
        const index = this.mutableValue.findIndex(x =>
          x === option
          || (
            typeof x === "object"
            && x.hasOwnProperty[this.reduceKey]
            && option.hasOwnProperty[this.reduceKey]
            && x[this.reduceKey] === option[this.reduceKey]
          )
        );

        if (index > -1) {
          this.mutableValue.splice(index, 1);
          try {
            this.popperInstance.update();
          } catch(err) {
            console.warn("[DOMException]", err);
          }
        }
      } else {
        this.mutableValue = null;
      }
      this.$emit('option:deselected', option);
    },
    /**
     * Clears the currently selected value(s)
     * @return {void}
     */
    clearSelection() {
      this.mutableValue = this.multiple ? [] : null;
      this.$emit('option:cleared');
    },
    /**
     * Called from this.select after each selection.
     * @param  {Object|String} option
     * @return {void}
     */
    onAfterSelect(option) {
      if (this.closeOnSelect) {
        this.open = !this.open;
        // to-do for single style issue
        !this.multiple && this.$refs.search.blur();
      }

      if (this.clearSearchOnSelect) {
        this.search = "";
      }
      if (this.noDrop && this.multiple) {
        this.$nextTick(() => this.$refs.search.focus());
      }
    },
    /**
     * Toggle the visibility of the dropdown menu.
     * @param  {Event} e
     * @return {void}
     */
    toggleDropdown(e) {
      if (
        e.target === this.$refs.openIndicator ||
        e.target === this.$refs.search ||
        e.target === this.$refs.toggle ||
        e.target.classList.contains("vs-selected-container") ||
        e.target.classList.contains("vs-selected-option") ||
        e.target.classList.contains("vs-selected-tag") ||
        e.target === this.$el
      ) {
        if (this.open) {
          this.$refs.search.blur(); // dropdown will close on blur
        } else {
          if (!this.disabled) {
            this.open = true;
            this.$refs.search.focus();
          }
        }
      }
    },
    /**
     * Check if the given option is currently selected.
     * @param  {Object|String}  option
     * @return {Boolean}        True when selected | False otherwise
     */
    isOptionSelected(option) {
      return this.valueAsArray.some((value) =>
        this.optionComparator(value, option)
      );
    },
    /**
     * Determine if two option objects are matching.
     *
     * @param a {Object}
     * @param b {Object}
     * @returns {boolean}
     */
    optionComparator(a, b) {
      return this.getOptionKey(a) === this.getOptionKey(b);
    },
    /**
     * Finds an option from this.options
     * where option[this.index] matches
     * the passed in value.
     *
     * @param value {Object}
     * @returns {*}
     */
    findOptionByIndexValue(value) {
      this.options.forEach((_option) => {
        if (JSON.stringify(_option[this.index]) === JSON.stringify(value)) {
          value = _option;
        }
      });
      return value;
    },
    /**
     * If there is any text in the search input, remove it.
     * Otherwise, blur the search input to close the dropdown.
     * @return {void}
     */
    onEscape(e) {
      if (!this.search.length) {
        this.open = false;
        this.$refs.search.blur();
      } else {
        this.search = "";
      }
      e.stopPropagation();
    },
    onSearchInput() {
      if (this.numberOfPageLoaded) {
        this.numberOfPageLoaded = 0;
      }
      this.$emit("search:input");
    },
    /**
     * Close the dropdown on blur.
     * @emits  {search:blur}
     * @return {void}
     */
    onSearchBlur() {
      if (this.mousedown && !this.searching) {
        this.mousedown = false;
      } else {
        const { clearSearchOnSelect, multiple } = this;
        if (this.clearSearchOnBlur({ clearSearchOnSelect, multiple})) {
          this.search = "";
        }
        this.closeSearchOptions();
        return
      }
      // Fixed bug where no-options message could not be closed
      if (this.search.length === 0 && this.options.length === 0) {
        this.closeSearchOptions();
        return
      }
    },
    /**
     * Open the dropdown on focus.
     * @emits  {search:focus}
     * @return {void}
     */
    onSearchFocus() {
      this.open = true;
      this.$emit("search:focus");
    },
    /**
     * 'Private' function to close the search options
     * @emits  {search:blur}
     * @returns {void}
     */
    closeSearchOptions() {
      this.open = false;
      this.$emit("search:blur");
    },
    /**
     * Delete the value on Delete keypress when there is no
     * text in the search input, & there's tags to delete
     * @return {this.value}
     */
    maybeDeleteValue() {
      if (!this.$refs.search.value.length && this.mutableValue) {
        if (this.multiple) {
          const selectedValue = [...this.mutableValue];
          this.deselect(selectedValue.pop());
        } else {
          this.clearSelection();
        }
      }
    },
    /**
     * Determine if an option exists
     * within this.mutableOptions array.
     *
     * @param  {Object || String} option
     * @return {boolean}
     */
    optionExists(option) {
      let exists = false;

      this.mutableOptions.forEach((opt) => {
        if (typeof opt === "object" && opt[this.label] === option) {
          exists = true;
        } else if (opt === option) {
          exists = true;
        }
      });

      return exists;
    },
    /**
     * If push-tags is true, push the
     * given option to mutableOptions.
     *
     * @param  {Object || String} option
     * @return {void}
     */
    maybePushTag(option) {
      if (this.pushTags) {
        this.mutableOptions.push(option);
      }
    },
    /**
     * Event-Handler to help workaround IE11 (probably fixes 10 as well)
     * firing a `blur` event when clicking
     * the dropdown's scrollbar, causing it
     * to collapse abruptly.
     * @return {void}
     */
    onMousedown() {
      this.mousedown = true;
    },
    /**
     * Event-Handler to help workaround IE11 (probably fixes 10 as well)
     * @see https://github.com/sagalbot/vue-select/issues/106
     * @return {void}
     */
    onMouseUp() {
      this.mousedown = false;
    },
    /**
     * Search <input> KeyBoardEvent handler.
     * @param {KeyboardEvent} e
     * @return {Function}
     */
    onSearchKeyDown(e) {
      const preventAndSelect = (e) => {
        e.preventDefault();
        return !this.isComposing && this.typeAheadSelect();
      }

      const defaults = {
        //  backspace
        8: (e) => this.maybeDeleteValue(),
        //  tab
        9: (e) => this.onTab(),
        //  esc
        27: (e) => this.onEscape(e),
        //  up.prevent
        38: (e) => {
          e.preventDefault()
          if (!this.open) {
            this.open = true;
            return
          }
          return this.typeAheadUp();
        },
        //  down.prevent
        40: (e) => {
          e.preventDefault();
          if (!this.open) {
            this.open = true;
            return
          }
          return this.typeAheadDown();
        },
      }

      this.selectOnKeyCodes.forEach(
        (keyCode) => (defaults[keyCode] = preventAndSelect)
      );

      const handlers = this.mapKeydown(defaults, this);

      if (typeof handlers[e.keyCode] === 'function') {
        return handlers[e.keyCode](e);
      }
    },
    /**
     * @todo: Probably want to add a mapKeyPress method just like we have for keydown.
     * @param {KeyboardEvent} e
     */
    onSearchKeyPress(e) {
      if (!this.open && e.keyCode === 32) {
        e.preventDefault();
        this.open = true;
      }
    }
  },
  computed: {
    mutableValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('updateValue', val);
        this.$emit('input', val);
      }
    },
    /**
     * Classes to be output on .dropdown
     * @return {Object}
     */
    dropdownClasses() {
      return {
        searching: this.searching,
        searchable: this.searchable,
        unsearchable: !this.searchable,
        rtl: this.dir === "rtl", // This can be removed - styling is handled by `dir="rtl"` attribute
        disabled: this.disabled,
        'vs--open': this.dropdownOpen,
        'vs--loading': this.mutableLoading,
        'vs--loading-more': this.loadingMore,
        'vs--single': !this.multiple,
        'vs--multiple': this.multiple,
        'vs--search-key': Boolean(this.search),
        'comfort-design': this.comfortDesign,
        'vs--error': this.error
      };
    },

    /**
     * Return the current state of the
     * search input
     * @return {Boolean} True if non empty value
     */
    searching() {
      return !!this.search && this.clearSearchOnSelect;
    },

    /**
     * Return the current state of the
     * dropdown menu.
     * @return {Boolean} True if open
     */
    dropdownOpen() {
      return this.noDrop ? false : this.open && !this.mutableLoading;
    },

    /**
     * Return the placeholder string if it's set
     * & there is no value selected.
     * @return {String} Placeholder text
     */
    searchPlaceholder() {
      if (this.disabled)
        return null;
      if ((this.isValueEmpty || this.multiple) && this.placeholder) {
        return this.placeholder;
      }
    },

    /**
     * The currently displayed options, filtered
     * by the search elements value. If tagging
     * true, the search text will be prepended
     * if it doesn't already exist.
     *
     * @return {array}
     */
    filteredOptions() {
      if (!this.filterable && !this.taggable) {
        return this.mutableOptions.slice();
      }
      let options = this.search.length
        ? this.filter(this.mutableOptions, this.search, this)
        : this.mutableOptions;
      if (
        this.taggable &&
        this.search.length &&
        !this.optionExists(this.search)
      ) {
        options.unshift(this.search);
      }
      return options;
    },
    shouldVirtualizeOptions() {
      return this.virtualized && this.filteredOptions.length > this.virtualizationThreshold;
    },
    virtualOptionHeight() {
      return this.measuredOptionHeight > 0 ? this.measuredOptionHeight : 40;
    },
    virtualVisibleCount() {
      const viewportHeight = this.dropdownViewportHeight || this.getDropdownHeightLimit();
      return Math.max(
        1,
        Math.ceil(viewportHeight / this.virtualOptionHeight) + (this.virtualizationOverscan * 2)
      );
    },
    virtualWindowStart() {
      if (!this.shouldVirtualizeOptions) {
        return 0;
      }

      return Math.max(
        0,
        Math.floor(this.virtualScrollTop / this.virtualOptionHeight) - this.virtualizationOverscan
      );
    },
    virtualWindowEnd() {
      if (!this.shouldVirtualizeOptions) {
        return this.filteredOptions.length;
      }

      return Math.min(
        this.filteredOptions.length,
        this.virtualWindowStart + this.virtualVisibleCount
      );
    },
    renderedOptions() {
      const start = this.shouldVirtualizeOptions ? this.virtualWindowStart : 0;
      const end = this.shouldVirtualizeOptions ? this.virtualWindowEnd : this.filteredOptions.length;

      return this.filteredOptions.slice(start, end).map((option, offset) => ({
        option,
        index: start + offset,
      }));
    },
    virtualTopSpacerHeight() {
      if (!this.shouldVirtualizeOptions) {
        return 0;
      }

      return this.virtualWindowStart * this.virtualOptionHeight;
    },
    virtualBottomSpacerHeight() {
      if (!this.shouldVirtualizeOptions) {
        return 0;
      }

      return Math.max(
        0,
        (this.filteredOptions.length - this.virtualWindowEnd) * this.virtualOptionHeight
      );
    },

    /**
     * Check if there aren't any options selected.
     * @return {Boolean}
     */
    isValueEmpty() {
      if (this.text) return false;

      if (this.mutableValue) {
        if (typeof this.mutableValue === "object") {
          return !Object.keys(this.mutableValue).length;
        }
        return !this.valueAsArray.length;
      }

      return true;
    },

    /**
     * Return the current value in array format.
     * @return {Array}
     */
    valueAsArray() {
      if (this.multiple && this.mutableValue) {
        return this.mutableValue;
      } else if (this.mutableValue) {
        return [].concat(this.mutableValue);
      }

      return [];
    },

    /**
     * Determines if the clear button should be displayed.
     * @return {Boolean}
     */
    showClearButton() {
      return (
        !this.multiple &&
        this.clearable &&
        !this.open &&
        (this.mutableValue != null || this.text != null)
      );
    },
  },
};
</script>